import { GitBranch, Globe, Lock, PenLine, X } from 'lucide-react'
import {
  Fragment,
  useCallback,
  useRef,
  type ComponentProps,
  type ReactNode,
  type Ref,
} from 'react'

import { CopyChip } from '@/components/CopyChip'
import { FactsLine } from '@/components/FactsLine'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

import { HiddenHintBadge } from '../OrgTooltips'
import type { BadgeId, SheetModel } from './model'
import { SharingChip } from './SharingChip'

const NAME_ERROR_ID = 'snippet-name-error'

const BADGES: Record<BadgeId, ReactNode> = {
  draft: <Badge variant="secondary">Draft</Badge>,
  shared: (
    <Badge>
      <Globe aria-hidden="true" />
      Shared
    </Badge>
  ),
  hidden: <HiddenHintBadge />,
  careforce: (
    <Badge variant="outline">
      <Lock aria-hidden="true" />
      Careforce
    </Badge>
  ),
  yourCopy: <Badge variant="success">Your copy</Badge>,
  outOfDate: <Badge variant="warning">Out of date</Badge>,
  variant: (
    <Badge>
      <GitBranch aria-hidden="true" />
      Variant
    </Badge>
  ),
}

type Sharing = Omit<ComponentProps<typeof SharingChip>, 'state' | 'className'>

/**
 * The name, edited in place, then what the snippet is (badges), how it is
 * used (facts), and how to reference it (ID and Token).
 */
export function SnippetSheetHeader({
  model,
  name,
  nameError,
  onNameChange,
  sharing,
}: {
  model: SheetModel | null
  name: string
  nameError: string | null
  onNameChange: (name: string) => void
  sharing: Sharing
}) {
  const chips = model?.chips ?? []
  const nameRef = useRef<HTMLInputElement>(null)
  // Save was disabled while it ran, so a rejected name has lost focus.
  const focusName = useCallback((error: HTMLElement | null) => {
    if (error) nameRef.current?.focus()
  }, [])
  return (
    <SheetHeader className="gap-0 px-4 pt-4 pb-2">
      <SheetTitle className="sr-only">{model?.title}</SheetTitle>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <NameField
              inputRef={nameRef}
              name={name}
              editable={Boolean(model?.nameEditable)}
              invalid={nameError !== null}
              onNameChange={onNameChange}
            />
            {model?.badges.map((id) => (
              <Fragment key={id}>{BADGES[id]}</Fragment>
            ))}
          </div>
          {model && <FactsLine facts={model.facts} />}
        </div>
        <SheetClose asChild>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Close snippet">
            <X />
          </Button>
        </SheetClose>
      </div>
      {nameError && (
        <p
          ref={focusName}
          id={NAME_ERROR_ID}
          role="alert"
          className="mt-1 text-sm text-destructive">
          {nameError}
        </p>
      )}
      {(chips.length > 0 || model?.sharing) && (
        <div className="-mx-0.5 mt-2.5 flex items-center gap-1.5 overflow-x-auto p-0.5 *:shrink-0 sm:flex-wrap">
          {chips.map((chip) => (
            <CopyChip key={chip.label} {...chip} />
          ))}
          {model?.sharing && <SharingChip state={model.sharing} {...sharing} />}
        </div>
      )}
    </SheetHeader>
  )
}

const NAME_TEXT = 'px-1.5 text-lg leading-7 font-semibold'

/**
 * Sized to its text so the badges sit beside it: an invisible copy of the
 * value shares the input's grid cell and sets the width.
 */
function NameField({
  inputRef,
  name,
  editable,
  invalid,
  onNameChange,
}: {
  inputRef: Ref<HTMLInputElement>
  name: string
  editable: boolean
  invalid: boolean
  onNameChange: (name: string) => void
}) {
  return (
    <div className="group/name relative -mx-1.5 inline-grid max-w-full min-w-24 grid-cols-[minmax(0,max-content)]">
      <span
        aria-hidden="true"
        className={cn(
          NAME_TEXT,
          'invisible col-start-1 row-start-1 truncate border border-transparent whitespace-pre',
          editable && 'pr-7'
        )}>
        {name || 'Untitled snippet'}
      </span>
      <Input
        ref={inputRef}
        aria-label="Snippet name"
        value={name}
        placeholder="Untitled snippet"
        autoComplete="off"
        readOnly={!editable}
        tabIndex={editable ? undefined : -1}
        aria-invalid={invalid}
        aria-describedby={invalid ? NAME_ERROR_ID : undefined}
        onChange={(event) => onNameChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          // Blurring to <body> would drop focus out of the dialog and break Cmd/Ctrl+Enter save.
          event.currentTarget.closest<HTMLElement>('[role="dialog"]')?.focus()
        }}
        className={cn(
          NAME_TEXT,
          'col-start-1 row-start-1 h-8 w-0 min-w-full border-transparent bg-transparent text-ellipsis',
          editable
            ? 'pr-7 hover:bg-app hover:not-focus-visible:border-neutral-ui-border focus-visible:border focus-visible:border-primary-solid focus-visible:bg-app focus-visible:ring-3 focus-visible:ring-primary-hover-bg'
            : 'hover:border-transparent focus-visible:border-transparent'
        )}
      />
      {editable && (
        <PenLine
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2 text-muted-foreground opacity-0 group-focus-within/name:opacity-100 group-hover/name:opacity-100"
        />
      )}
    </div>
  )
}
